import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveLine } from '@nivo/line';
import { getChartColors, getNivoCommonProps } from '../../utils/nivoTheme';
import {
  formatAxisNumber,
  TooltipWrapper,
  hasNegativeValues,
  createZeroLineMarker
} from '../../utils/chartUtils';
import i18n from '../../i18n/config.js';
import './CashflowChart.css';

const BASELINE_SERIES_ID = 'baseline';

/**
 * Keep days from today through the selected forward range.
 *
 * @param {Array|null} rows - Cashflow or baseline days
 * @param {number} selectedDays - Forward range length
 * @returns {Array}
 */
const filterToSelectedRange = (rows, selectedDays) => {
  if (!rows || rows.length === 0) return [];

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const endDate = new Date(today);
  endDate.setDate(today.getDate() + selectedDays - 1);

  return rows.filter((day) => {
    const dayDate = new Date(day.date);
    dayDate.setHours(0, 0, 0, 0);
    return dayDate >= today && dayDate <= endDate;
  });
};

/**
 * Draw the scenario area and line, and the baseline as a dashed guide.
 * Nivo colors a whole series, so a line that crosses zero is clipped instead.
 *
 * @param {string} positiveColor - Color for scenario values above 0
 * @param {string} negativeColor - Color for scenario values below 0
 * @param {string} seriesColor - Scenario color when the line does not cross zero
 * @param {string} guideColor - Baseline guide stroke
 * @param {boolean} splitAtZero - Clip the scenario series at y = 0
 * @returns {Function} Nivo custom layer
 */
const createCashflowSeriesLayer = (
  positiveColor,
  negativeColor,
  seriesColor,
  guideColor,
  splitAtZero
) => (props) => {
  const {
    series,
    lineGenerator,
    areaGenerator,
    yScale,
    innerWidth,
    innerHeight,
    areaOpacity
  } = props;

  const zeroY = yScale(0);
  const clipTop = Math.max(0, Math.min(zeroY, innerHeight));
  const clipBottomHeight = Math.max(0, innerHeight - clipTop);

  return (
    <g>
      {splitAtZero && (
        <defs>
          <clipPath id="cashflow-clip-above">
            <rect x={0} y={0} width={innerWidth} height={clipTop} />
          </clipPath>
          <clipPath id="cashflow-clip-below">
            <rect x={0} y={clipTop} width={innerWidth} height={clipBottomHeight} />
          </clipPath>
        </defs>
      )}
      {series.map((serie) => {
        const positions = serie.data.map((d) => d.position);
        const linePath = lineGenerator(positions);
        if (serie.id === BASELINE_SERIES_ID) {
          return (
            <path
              key={serie.id}
              d={linePath}
              fill="none"
              stroke={guideColor}
              strokeWidth={1.5}
              strokeDasharray="4 4"
            />
          );
        }

        const areaPath = areaGenerator(positions);
        if (!splitAtZero) {
          return (
            <g key={serie.id}>
              <path d={areaPath} fill={seriesColor} fillOpacity={areaOpacity} strokeWidth={0} />
              <path d={linePath} fill="none" stroke={seriesColor} strokeWidth={2} />
            </g>
          );
        }

        return (
          <g key={serie.id}>
            <path d={areaPath} fill={positiveColor} fillOpacity={areaOpacity} strokeWidth={0} clipPath="url(#cashflow-clip-above)" />
            <path d={areaPath} fill={negativeColor} fillOpacity={areaOpacity} strokeWidth={0} clipPath="url(#cashflow-clip-below)" />
            <path d={linePath} fill="none" stroke={positiveColor} strokeWidth={2} clipPath="url(#cashflow-clip-above)" />
            <path d={linePath} fill="none" stroke={negativeColor} strokeWidth={2} clipPath="url(#cashflow-clip-below)" />
          </g>
        );
      })}
    </g>
  );
};

/**
 * CashflowChart component - combined line and bar chart for cashflow visualization
 * @param {Object} props - Component props
 * @param {Array} props.data - Active cashflow days (scenario when a toggle is on)
 * @param {Array|null} props.baseline - Core-cash guide points, or null when no toggle is on
 * @param {number} props.selectedDays - Active forward range (30 | 45 | 60)
 */
const CashflowChart = ({ data, baseline, selectedDays }) => {
  const { t } = useTranslation();
  const locale = i18n.language === 'no' ? 'no-NO' : 'en-US';

  // Get common Nivo props
  const commonProps = getNivoCommonProps();
  const chartColors = getChartColors();

  // Format currency for display
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'NOK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Format currency for y-axis (compact format with K/M notation, 2 decimals for millions only)
  const formatCurrencyCompact = (value) => {
    if (value === null || value === undefined) return '';
    const formatted = formatAxisNumber(value, 2);
    // Add currency symbol if not already present (formatAxisNumber returns just the number with K/M)
    return `${formatted} kr`;
  };

  const filteredData = useMemo(
    () => filterToSelectedRange(data, selectedDays),
    [data, selectedDays]
  );
  const filteredBaseline = useMemo(
    () => filterToSelectedRange(baseline, selectedDays),
    [baseline, selectedDays]
  );
  const showGuide = filteredBaseline.length > 0;

  const lineData = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return [];

    const toPoints = (rows) => rows.map((day) => ({
      x: day.date,
      y: day.available_cash
    }));
    const activeSeries = {
      id: showGuide ? 'scenario' : 'available_cash',
      data: toPoints(filteredData)
    };
    if (!showGuide) return [activeSeries];

    return [
      { id: BASELINE_SERIES_ID, data: toPoints(filteredBaseline) },
      activeSeries
    ];
  }, [filteredData, filteredBaseline, showGuide]);

  // Calculate y-axis scale from the active line and the guide, when shown
  const { yScaleMin, yScaleMax } = useMemo(() => {
    const cashValues = [
      ...filteredData.map((day) => day.available_cash),
      ...(showGuide ? filteredBaseline.map((day) => day.available_cash) : [])
    ].filter((v) => v != null);

    if (cashValues.length === 0) {
      return { yScaleMin: 0, yScaleMax: 100000 };
    }

    const min = Math.min(...cashValues);
    const max = Math.max(...cashValues);
    
    // Add padding (10% on each side)
    const range = max - min;
    const padding = Math.max(range * 0.1, Math.abs(max) * 0.05);
    
    return {
      yScaleMin: min - padding,
      yScaleMax: max + padding
    };
  }, [filteredData, filteredBaseline, showGuide]);

  const activeSeries = lineData.filter((series) => series.id !== BASELINE_SERIES_ID);
  const hasNegatives = hasNegativeValues(activeSeries, null, 'line');
  const scaleCrossesZero = yScaleMin < 0 && yScaleMax > 0;
  const splitAtZero = hasNegatives && scaleCrossesZero;
  const positiveColor = chartColors[1];
  const negativeColor = chartColors[0];
  const seriesColor = hasNegatives && !splitAtZero ? negativeColor : positiveColor;
  const guideColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--color-text-tertiary')
    .trim();
  const useCustomLayer = showGuide || splitAtZero;

  // Custom tooltip showing available cash and events
  const CustomTooltip = ({ slice }) => {
    if (!slice || !slice.points || slice.points.length === 0) {
      return null;
    }
    
    const activePoint = slice.points.find((point) => point.seriesId !== BASELINE_SERIES_ID);
    if (!activePoint) return null;
    const date = activePoint.data.x;
    
    // Find the day data - handle both string dates and Date objects
    const dayData = filteredData.find(d => {
      const dayDateStr = typeof d.date === 'string' ? d.date : d.date.toISOString().split('T')[0];
      const compareDateStr = typeof date === 'string' ? date : date.toISOString().split('T')[0];
      return dayDateStr === compareDateStr;
    });
    
    if (!dayData) return null;

    const tooltipContent = (
      <div style={{
        background: 'var(--color-bg-primary)',
        border: '1px solid var(--color-border-light)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-lg)',
        padding: 'var(--spacing-4)',
        minWidth: '200px',
        maxWidth: '400px'
      }}>

        <div style={{
          color: 'var(--color-text-secondary)',
          fontWeight: 500,
          marginBottom: 'var(--spacing-1)',
          fontSize: 'var(--font-size-xs)'
        }}>
          {t('overview.tooltipCashflowScope')}
        </div>
        <div style={{
          color: 'var(--color-text-primary)',
          fontWeight: 600,
          marginBottom: 'var(--spacing-2)',
          fontSize: 'var(--font-size-base)'
        }}>
          {new Date(date).toLocaleDateString(locale, { 
            day: 'numeric', 
            month: 'long', 
            year: 'numeric' 
          })}
        </div>
        
        {/* Available cash - always show */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
          marginBottom: 'var(--spacing-2)',
          fontSize: 'var(--font-size-sm)',
          whiteSpace: 'nowrap'
        }}>
          <span style={{ color: 'var(--color-text-secondary)' }}>{t('overview.availableCash')}:</span>
          <strong>{formatCurrency(dayData.available_cash)}</strong>
        </div>

        {/* Events - filter out no_event */}
        {dayData.events
          .filter(event => event.event !== 'no_event')
          .map((event, index) => {
            const isProjected = event.event === 'projected_outbound_invoice';
            const isOverdue = event.event === 'overdue_outbound_invoice';
            const amountColor = isOverdue
              ? 'var(--chart-color-10)'
              : isProjected
                ? 'var(--color-blue-dark)'
                : event.amount < 0
                  ? 'var(--color-outgoing)'
                  : event.amount > 0
                    ? 'var(--color-incoming)'
                    : 'var(--color-text-primary)';

            return (
            <div key={index}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-2)',
                marginBottom: 'var(--spacing-2)',
                fontSize: 'var(--font-size-sm)',
                whiteSpace: 'nowrap'
              }}>
                <span style={{ color: 'var(--color-text-secondary)' }}>
                  {t(`overview.eventNames.${event.event}`, event.event.replace(/_/g, ' '))}:
                </span>
                <strong style={{ color: amountColor }}>
                  {formatCurrency(event.amount)}
                </strong>
              </div>
              
              {/* Invoice details */}
              {event.invoices && event.invoices.length > 0 && (
                <div style={{
                  marginLeft: 'var(--spacing-4)',
                  marginTop: 'var(--spacing-1)',
                  fontSize: 'var(--font-size-xs)',
                  color: isOverdue
                    ? 'var(--chart-color-10)'
                    : isProjected
                      ? 'var(--color-blue-dark)'
                      : 'var(--color-text-tertiary)'
                }}>
                  {event.invoices.map((invoice, invIndex) => (
                    <div key={invIndex} style={{ marginBottom: 'var(--spacing-1)' }}>
                      • {isProjected ? invoice.project_name : (invoice.supplier_name || invoice.customer_name)}: {formatCurrency(invoice.amount)}
                    </div>
                  ))}
                </div>
              )}
            </div>
            );
          })}
      </div>
    );

    return tooltipContent;
  };

  if (!data || data.length === 0) {
    return null;
  }

  return (
    <div className="cashflow-chart-container">
      {showGuide && (
        <p className="cashflow-guide-legend">{t('overview.guideLegend')}</p>
      )}
      <div className="cashflow-chart-wrapper">
        {/* Line chart for available_cash */}
        <div className="cashflow-line-chart">
          <ResponsiveLine
            {...commonProps}
            data={lineData}
            margin={{ top: 20, right: 20, bottom: 60, left: 60 }}
            xScale={{ type: 'point' }}
            yScale={{ type: 'linear', min: yScaleMin, max: yScaleMax, nice: false }}
            areaBaselineValue={scaleCrossesZero ? 0 : yScaleMin}
            markers={hasNegatives ? createZeroLineMarker() : []}
            layers={useCustomLayer
              ? [
                  'grid',
                  'markers',
                  'axes',
                  createCashflowSeriesLayer(
                    positiveColor,
                    negativeColor,
                    seriesColor,
                    guideColor,
                    splitAtZero
                  ),
                  'crosshair',
                  'slices',
                  'mesh',
                  'legends'
                ]
              : undefined
            }
            axisTop={null}
            axisRight={null}
            axisBottom={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: -45,
              format: (value) => {
                const date = new Date(value);
                return date.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
              }
            }}
            axisLeft={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              format: (value) => formatCurrencyCompact(value)
            }}
            enablePoints={false}
            enableArea={true}
            areaOpacity={0.15}
            useMesh={true}
            enableSlices="x"
            sliceTooltip={({ slice }) => {
              // Find the index of the current point in the data to determine left/right position
              const firstSeriesData = activeSeries[0]?.data || [];
              const activePoint = slice.points.find((point) => point.seriesId !== BASELINE_SERIES_ID);
              const currentX = activePoint?.data?.x;
              // Find index by comparing x values (handle both string and number comparisons)
              const pointIndex = firstSeriesData.findIndex(p => String(p.x) === String(currentX));
              
              return (
                <TooltipWrapper dataIndex={pointIndex} dataLength={firstSeriesData.length}>
                  <CustomTooltip slice={slice} />
                </TooltipWrapper>
              );
            }}
            isInteractive={true}
            enableCrosshair={true}
            colors={[seriesColor]}
            legends={[]}
          />
        </div>
      </div>
    </div>
  );
};

export default CashflowChart;
